type TranslateFn = (key: string) => string | undefined;

const NESTED_QUESTION_KEY_MAP: Record<string, string> = {
  'What reaction do you have to this medication?': 'label-what-reaction-do-you-have',
  'What happens when you take this?': 'label-what-happens-when-you-take-this',
  'What happens to your body when you are exposed to your allergen?':
    'label-what-happens-to-your-body-when-exposed',
  'What side effects do you experience?': 'label-what-side-effects-do-you-experience',
  'How do you react to this?': 'label-how-do-you-react-to-this',
  'What symptoms do you have?': 'label-what-symptoms-do-you-have',
  'What are your symptoms?': 'label-what-are-your-symptoms',
  'How severe are your symptoms?': 'label-how-severe-are-your-symptoms',
  'When do your symptoms occur?': 'label-when-do-your-symptoms-occur',
  'How long do your symptoms last?': 'label-how-long-do-your-symptoms-last',
  'What triggers your symptoms?': 'label-what-triggers-your-symptoms',
  'How do you manage your symptoms?': 'label-how-do-you-manage-your-symptoms',
  'What medications are you taking?': 'label-what-medications-are-you-taking',
  'What are the side effects?': 'label-what-are-the-side-effects',
  'How often do you take this?': 'label-how-often-do-you-take-this',
  'What dose are you taking?': 'label-what-dose-are-you-taking',
  'When did you start taking this?': 'label-when-did-you-start-taking-this',
  'Why are you taking this?': 'label-why-are-you-taking-this',
  'How effective is this medication?': 'label-how-effective-is-this-medication',
  'What allergies do you have?': 'label-what-allergies-do-you-have',
  'What happens when you have an allergic reaction?':
    'label-what-happens-when-you-have-an-allergic-reaction',
  'How severe is your allergic reaction?': 'label-how-severe-is-your-allergic-reaction',
  'What do you do when you have an allergic reaction?':
    'label-what-do-you-do-when-you-have-an-allergic-reaction',
  'When did you first notice this allergy?': 'label-when-did-you-first-notice-this-allergy',
  'How was this allergy diagnosed?': 'label-how-was-this-allergy-diagnosed',
};

function resolveTranslationKey(questionText: string, t: TranslateFn): string | null {
  const normalized = normalizeComparableText(questionText);
  const mapped = NESTED_QUESTION_KEY_MAP_BY_NORMALIZED[normalized];
  if (mapped) {
    return mapped;
  }

  const fuzzyMapped = resolveFuzzyMappedKey(normalized);
  if (fuzzyMapped) {
    return fuzzyMapped;
  }

  const direct = t(questionText);
  if (direct && direct !== questionText) {
    return questionText;
  }

  return null;
}

function normalizeComparableText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

const NESTED_QUESTION_KEY_MAP_BY_NORMALIZED = Object.entries(NESTED_QUESTION_KEY_MAP).reduce<
  Record<string, string>
>((acc, [questionText, key]) => {
  const normalized = normalizeComparableText(questionText);
  acc[normalized] = key;
  return acc;
}, {});

function resolveFuzzyMappedKey(normalizedQuestionText: string): string | null {
  if (!normalizedQuestionText) {
    return null;
  }

  if (/\bwhat reaction do you have to(?: this)? medication\b/.test(normalizedQuestionText)) {
    return 'label-what-reaction-do-you-have';
  }

  return null;
}

function isSameAsSourceText(candidate: string, source: string): boolean {
  if (!candidate || !source) {
    return false;
  }
  return normalizeComparableText(candidate) === normalizeComparableText(source);
}

export function resolveNestedQuestionLabel(params: {
  questionText?: string | null;
  t: TranslateFn;
  sltStrByKey: TranslateFn;
}): string {
  const { questionText, t, sltStrByKey } = params;
  const normalized = (questionText || '').trim();

  if (!normalized) {
    return '';
  }

  const translationKey = resolveTranslationKey(normalized, t);
  if (!translationKey) {
    return normalized;
  }

  const surveyTranslated = t(translationKey);
  if (
    surveyTranslated &&
    surveyTranslated !== translationKey &&
    !isSameAsSourceText(surveyTranslated, normalized)
  ) {
    return surveyTranslated;
  }

  const staticTranslated = sltStrByKey(translationKey);
  if (staticTranslated && staticTranslated !== translationKey) {
    return staticTranslated;
  }

  if (surveyTranslated && surveyTranslated !== translationKey) {
    return surveyTranslated;
  }

  return normalized;
}
